/**
 * User Form Dialog (PR A17)
 * Create and Edit user dialog with role assignment
 * 
 * RBAC Change (Jan 28, 2026): Single role per user enforcement
 * - Changed from multi-select checkboxes to single-select radio buttons
 * - Each user can only have ONE active role at a time
 */
import React, { useState, useEffect } from 'react';
import { createUser, updateUser, assignRoles, listRoles, updateUserQCRequirement, updateUserFinanceVisibility, getRoleCompatibility } from '../../utils/usersApi';
import { DEFAULT_COMPAT, canonicalRole, isLegacyRole, roleLabel, canHoldAdditional, offerableAdditional, blockReason, reconcileAdditional } from '../../utils/roleCompatibility';
import { userActionToasts, toast } from '../../utils/toast';
import {
  UserPlus, UserRoundPen, X, Mail, User, MailCheck, Info, ArrowRight, ArrowLeft, Loader2, Check, AlertTriangle,
  Crown, Package, FileText, Search, Keyboard, Users, Landmark, MapPin, Wrench, Server, HeartHandshake, Briefcase, Calculator, BadgeCheck, Megaphone, ShieldCheck, Send,
} from 'lucide-react';
import './UserFormDialog.premium.css';

// USERS-UI-005: role card visuals (presentation only — role ids/definitions come from /roles)
const ROLE_CARD = {
  admin:                    { Icon: Crown,          bg: '#ede9fe', fg: '#6d28d9' },
  order_desk:               { Icon: Package,        bg: '#dcfce7', fg: '#15803d' },
  processor:                { Icon: FileText,       bg: '#e0e7ff', fg: '#4338ca' },
  search_processor:         { Icon: Search,         bg: '#fff7ed', fg: '#ea580c' },
  typing_processor:         { Icon: Keyboard,       bg: '#e0f2fe', fg: '#0369a1' },
  tax_search_team_leader:   { Icon: Users,          bg: '#fef3c7', fg: '#b45309' },
  title_search_team_leader: { Icon: Users,          bg: '#ffedd5', fg: '#c2410c' },
  team_leader:              { Icon: Users,          bg: '#f1f5f9', fg: '#475569' },
  jurisdiction_manager:     { Icon: Landmark,       bg: '#ede9fe', fg: '#7c3aed' },
  jurisdiction_requestor:   { Icon: MapPin,         bg: '#e0e7ff', fg: '#4f46e5' },
  it_support:               { Icon: Wrench,         bg: '#f1f5f9', fg: '#475569' },
  it_manager:               { Icon: Server,         bg: '#e2e8f0', fg: '#334155' },
  hr:                       { Icon: HeartHandshake, bg: '#fce7f3', fg: '#be185d' },
  hr_manager:               { Icon: Briefcase,      bg: '#fae8ff', fg: '#a21caf' },
  accounting:               { Icon: Calculator,     bg: '#d1fae5', fg: '#047857' },
  qc_specialist:            { Icon: BadgeCheck,     bg: '#fce7f3', fg: '#db2777' },
  sales_marketing:          { Icon: Megaphone,      bg: '#ede9fe', fg: '#5b21b6' },
};
const roleCard = (id) => ROLE_CARD[id] || { Icon: ShieldCheck, bg: '#eef2ff', fg: '#4f46e5' };
const STEPS = [
  { n: 1, title: 'Account Information', sub: 'Basic details and email' },
  { n: 2, title: 'Role & Permissions', sub: 'Assign role and access' },
  { n: 3, title: 'Review & Invite', sub: 'Confirm and create user' },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Roles that require team assignment for leave routing
const FINANCE_FLAG_ROLES = ['title_search_team_leader', 'tax_search_team_leader', 'team_leader']; // FIN-VIS-001
const TEAM_REQUIRED_ROLES = ['processor', 'search_processor', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader', 'title_search_processor', 'sales_marketing'];

// Map display names to role IDs
const ROLE_NAME_TO_ID = {
  'Admin': 'admin',
  'Order Desk': 'order_desk',
  'Tax Processor': 'processor',
  'Search Processor': 'search_processor',
  'Typing Processor': 'typing_processor',
  'Tax Search Team Leader': 'tax_search_team_leader',
  'Title Search Team Leader': 'title_search_team_leader',
  'Team Leader': 'team_leader',
  'Jurisdiction Manager': 'jurisdiction_manager',
  'Jurisdiction Requestor': 'jurisdiction_requestor',
  'IT Support': 'it_support',
  'IT Manager': 'it_manager',
  'HR Specialist': 'hr',
  'HR Manager': 'hr_manager',
  'Accounting': 'accounting',
  'QC Specialist': 'qc_specialist',
  'Sales & Marketing': 'sales_marketing',
  // Also support lowercase role IDs directly
  'admin': 'admin',
  'order_desk': 'order_desk',
  'processor': 'processor',
  'search_processor': 'search_processor',
  'typing_processor': 'typing_processor',
  'tax_search_team_leader': 'tax_search_team_leader',
  'title_search_team_leader': 'title_search_team_leader',
  'team_leader': 'team_leader',
  'jurisdiction_manager': 'jurisdiction_manager',
  'jurisdiction_requestor': 'jurisdiction_requestor',
  'it_support': 'it_support',
  'it_manager': 'it_manager',
  'hr': 'hr',
  'hr_manager': 'hr_manager',
  'accounting': 'accounting',
  'qc_specialist': 'qc_specialist',
  'sales_marketing': 'sales_marketing'
};

// Helper to get single role ID from user (for backward compatibility)
const getSingleRoleId = (user) => {
  // First try role_ids if available (take first one)
  if (user.role_ids?.length > 0) {
    return user.role_ids[0];
  }
  
  // Then try roles array (may contain display names)
  if (user.roles?.length > 0) {
    return ROLE_NAME_TO_ID[user.roles[0]] || user.roles[0];
  }
  
  // Finally try single role field
  if (user.role) {
    return ROLE_NAME_TO_ID[user.role] || user.role;
  }
  
  return '';
};

// Roles where QC Required toggle is relevant
// ROLE-COMPAT-001: additional-role eligibility now comes from utils/roleCompatibility (matrix served by the API)
const QC_APPLICABLE_ROLES = ['processor', 'tax_search_team_leader', 'search_processor'];

const UserFormDialog = ({ 
  isOpen, 
  onClose, 
  user = null,  // null for create, user object for edit
  onSuccess 
}) => {
  const isEdit = !!user;
  
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    role_id: '',  // Primary role
    additional_role_ids: [],  // USER-DUAL-ROLE-UI-001: approved operational roles held in addition
    requires_qc: true,  // Default true for new users
    view_search_pricing_margin: false,  // FIN-VIS-001: default OFF
    team_id: ''  // Team assignment for leave routing
  });
  
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [step, setStep] = useState(1);
  const [dir, setDir] = useState('fwd');
  // ROLE-COMPAT-001
  const [compat, setCompat] = useState(DEFAULT_COMPAT);
  const [storedPrimary, setStoredPrimary] = useState('');       // exact key stored on the user (may be legacy)
  const [qcScope, setQcScope] = useState([]);                   // user's QC Product Scope (read-only here)
  const [removedRoles, setRemovedRoles] = useState([]);         // [{ role, reason }] dropped as incompatible

  useEffect(() => {
    if (isOpen) {
      fetchRoles();
      fetchTeams();
      getRoleCompatibility().then((c) => c && c.dual_role_primaries && setCompat(c)).catch(() => {});
      if (isEdit && user) {
        const heldKeys = Array.isArray(user.role_ids) && user.role_ids.length ? user.role_ids : (user.roles || []).map((n) => ROLE_NAME_TO_ID[n] || n);
        const primaryStored = heldKeys[0] || getSingleRoleId(user);
        const scope = Array.isArray(user.qc_product_scope) ? user.qc_product_scope : [];
        const { kept, removed } = reconcileAdditional(primaryStored, heldKeys.slice(1), scope, compat);
        setStoredPrimary(primaryStored);
        setQcScope(scope);
        setRemovedRoles(removed.map((r) => ({ ...r, flagged: true })));
        setFormData({
          email: user.email || '',
          first_name: user.first_name || '',
          last_name: user.last_name || '',
          password: '',
          confirm_password: '',
          // Legacy keys (title_search_processor) display as their canonical card; the stored key is preserved on save
          role_id: canonicalRole(primaryStored, compat),
          additional_role_ids: kept,
          requires_qc: user.requires_qc !== false,  // Default true if not set
          view_search_pricing_margin: user.view_search_pricing_margin === true,
          team_id: user.team_id || ''
        });
      } else {
        setStoredPrimary('');
        setQcScope([]);
        setRemovedRoles([]);
        // Reset for create
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          password: '',
          confirm_password: '',
          role_id: '',
          additional_role_ids: [],
          requires_qc: true,  // Default true for new users
          view_search_pricing_margin: false,
          team_id: ''
        });
      }
      setErrors({});
      setGeneralError('');
      setStep(1);
      setDir('fwd');
    }
  }, [isOpen, user, isEdit]);

  const fetchRoles = async () => {
    try {
      const rolesData = await listRoles();
      setRoles(rolesData);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/teams`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTeams(data.teams || []);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.first_name) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name) {
      newErrors.last_name = 'Last name is required';
    }
    
    // Single role validation
    if (!formData.role_id) {
      newErrors.role_id = 'A role must be selected';
    }
    
    // Team assignment validation for routing-required roles
    if (TEAM_REQUIRED_ROLES.includes(formData.role_id) && !formData.team_id) {
      newErrors.team_id = 'Team assignment is required for this role (used for leave routing)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Per-step validation (same rules as validateForm, split by step)
  const validateStep = (n) => {
    const e = {};
    if (n === 1) {
      if (!formData.email) e.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Email is invalid';
      if (!formData.first_name) e.first_name = 'First name is required';
      if (!formData.last_name) e.last_name = 'Last name is required';
      if (!formData.role_id) e.role_id = 'A role must be selected';
    }
    if (n === 2 && TEAM_REQUIRED_ROLES.includes(formData.role_id) && !formData.team_id) {
      e.team_id = 'Team assignment is required for this role (used for leave routing)';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const advancedAtRef = React.useRef(0);
  const goNext = () => { if (validateStep(step)) { advancedAtRef.current = Date.now(); setDir('fwd'); setStep((s) => Math.min(3, s + 1)); } };
  const goBack = () => { setDir('back'); setStep((s) => Math.max(1, s - 1)); };
  const goTo = (n) => { if (n < step) { setDir('back'); setStep(n); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 3) { goNext(); return; }
    if (Date.now() - advancedAtRef.current < 400) return; // ignore a submit riding on the Continue click that just advanced to step 3
    
    if (!validateForm()) {
      setDir('back'); setStep(1);
      return;
    }
    
    // Clear previous errors
    setErrors({});
    setGeneralError('');
    
    try {
      setLoading(true);
      
      if (isEdit) {
        // Update user basic info + team assignment
        await updateUser(user.id, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          ...(formData.team_id ? { team_id: formData.team_id } : {})
        });
        
        // Update role (single role as array for API compatibility)
        // Detect whether the role actually changed so we only show the
        // "must re-login" notice when it's relevant.
        const previousRoleIds = (user.role_ids && user.role_ids.length ? user.role_ids : [user.role_id].filter(Boolean));
        const nextRoleIds = [primaryToSend, ...formData.additional_role_ids.filter((r) => canonicalRole(r, compat) !== formData.role_id)];
        const roleChanged = previousRoleIds.join(',') !== nextRoleIds.join(',');

        try {
          await assignRoles(user.id, nextRoleIds);
        } catch (roleError) {
          console.error('Role assignment failed:', roleError);
          setGeneralError(`Failed to update role: ${roleError.message}`);
          toast.error(`Failed to update role: ${roleError.message}`);
          return;
        }

        // Soft-sync notice (Feb 27, 2026): the target user's JWT still
        // carries the old role claim until they re-login, so let the
        // admin know explicitly.
        if (roleChanged) {
          toast.info(
            'Role updated successfully. The user must log out and log back in for the new permissions to fully apply.',
            { duration: 7000, 'data-testid': 'role-change-relogin-toast' }
          );
        }
        
        // FIN-VIS-001: per-user financial visibility (eligible team-leader roles only; default OFF)
        if (FINANCE_FLAG_ROLES.includes(formData.role_id) || user.view_search_pricing_margin) {
          try {
            await updateUserFinanceVisibility(user.id, FINANCE_FLAG_ROLES.includes(formData.role_id) && formData.view_search_pricing_margin);
          } catch (finErr) {
            console.error('Finance visibility update failed:', finErr);
          }
        }

        // Update QC requirement if applicable
        if (QC_APPLICABLE_ROLES.includes(formData.role_id) && formData.requires_qc !== (user.requires_qc ?? true)) {
          try {
            await updateUserQCRequirement(user.id, formData.requires_qc);
          } catch (qcError) {
            console.error('QC requirement update failed:', qcError);
            // Non-blocking — role and name were already saved
          }
        }
        
        toast.success('User updated successfully');
      } else {
        // Create user with single role + team assignment
        // P0 QC Workflow bug fix (Feb 26, 2026): include requires_qc in the
        // payload so the toggle state is persisted on creation. Only sent for
        // QC-applicable roles; for other roles the backend defaults to true
        // (which is fine since the flag is unused for them).
        await createUser({
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role_ids: [formData.role_id],  // Single role as array for API compatibility
          require_otp_verification: true,
          ...(formData.team_id ? { team_id: formData.team_id } : {}),
          ...(QC_APPLICABLE_ROLES.includes(formData.role_id)
            ? { requires_qc: formData.requires_qc }
            : {})
        });
        
        toast.success('User created successfully. Verification email sent to ' + formData.email);
      }
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error('User form error:', err);
      
      // Check for 409 Conflict (duplicate email)
      const is409Error = err.status === 409 || 
                         err.message?.includes('409') || 
                         err.message?.toLowerCase().includes('already exists') ||
                         err.message?.toLowerCase().includes('duplicate');
      
      if (is409Error) {
        const errorMessage = 'This email already exists. Please use a different email.';
        setErrors({ email: errorMessage });
        setGeneralError('A user with this email already exists. Please use a different email address.');
        setDir('back'); setStep(1);
        toast.error('A user with this email already exists');
      } else if (err.message?.includes('403') || err.status === 403) {
        setGeneralError('Permission denied. You do not have access to perform this action.');
        userActionToasts.permissionDenied();
      } else {
        setGeneralError(`Something went wrong while ${isEdit ? 'updating' : 'creating'} the user. Please try again or contact support.`);
        toast.error(`Failed to ${isEdit ? 'update' : 'create'} user: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle single role selection (radio button behavior)
  // ROLE-COMPAT-001: changing the primary drops additional roles that are no longer compatible, with a notice.
  const handleRoleSelect = (roleId) => {
    setFormData(prev => {
      const { kept, removed } = reconcileAdditional(roleId, prev.additional_role_ids || [], qcScope, compat);
      setRemovedRoles((old) => [...old.filter((o) => o.flagged && !kept.includes(o.role)), ...removed.map((r) => ({ ...r, flagged: false }))]);
      return { ...prev, role_id: roleId, additional_role_ids: kept };
    });
  };

  const toggleAdditional = (roleId, checked) => {
    setFormData((prev) => ({ ...prev, additional_role_ids: checked ? [...prev.additional_role_ids, roleId] : prev.additional_role_ids.filter((x) => x !== roleId) }));
    if (checked) setRemovedRoles((old) => old.filter((o) => o.role !== roleId));
  };

  // Send the stored legacy key back unchanged when the admin kept the same (canonical) primary card selected.
  const primaryToSend = storedPrimary && canonicalRole(storedPrimary, compat) === formData.role_id ? storedPrimary : formData.role_id;

  if (!isOpen) return null;

  const selectedRole = roles.find((r) => r.id === formData.role_id) || null;
  const needsTeam = TEAM_REQUIRED_ROLES.includes(formData.role_id);
  const qcApplies = QC_APPLICABLE_ROLES.includes(formData.role_id);
  const financeApplies = isEdit && FINANCE_FLAG_ROLES.includes(formData.role_id); // FIN-VIS-001
  const selectedTeam = teams.find((t) => t.id === formData.team_id) || null;
  const stepState = (n) => (n === step ? 'active' : n < step ? 'done' : 'todo');
  const heading = { 1: ['Account Information', 'Enter the basic details for the new user.'],
                    2: ['Role & Permissions', 'Review the access this role grants and complete any required assignments.'],
                    3: [isEdit ? 'Review & Update' : 'Review & Invite', isEdit ? 'Confirm the changes before saving.' : 'Confirm the details — the user will receive an invitation email.'] }[step];
  const RoleIcon = selectedRole ? roleCard(selectedRole.id).Icon : ShieldCheck;

  return (
    <>
      <div className="ufd-backdrop" onClick={loading ? undefined : onClose} data-testid="user-form-backdrop" />

      <form className="ufd" onSubmit={handleSubmit} role="dialog" aria-modal="true" aria-labelledby="ufd-title" data-testid="user-form-dialog" data-step={step} noValidate>
        <aside className="ufd-rail">
          <div className="ufd-rail-icon">{isEdit ? <UserRoundPen className="w-8 h-8" /> : <UserPlus className="w-8 h-8" />}</div>
          <h2 id="ufd-title" data-testid="user-form-title">{isEdit ? 'Edit User' : 'Create New User'}</h2>
          <p>{isEdit ? 'Update the account details, role and access for this user.' : 'Add a new user to the system with appropriate roles and permissions.'}</p>
          <ol className="ufd-steps" data-testid="user-form-steps">
            {STEPS.map((st) => (
              <li key={st.n} className="ufd-step" data-state={stepState(st.n)} data-testid={`user-form-step-${st.n}`}>
                <span className="ufd-step-num" aria-hidden="true">{stepState(st.n) === 'done' ? <Check className="w-5 h-5" /> : st.n}</span>
                <button type="button" onClick={() => goTo(st.n)} disabled={st.n >= step} aria-current={st.n === step ? 'step' : undefined}>
                  <div className="ufd-step-title">{st.n === 3 && isEdit ? 'Review & Update' : st.title}</div>
                  <div className="ufd-step-sub">{st.n === 3 && isEdit ? 'Confirm and save changes' : st.sub}</div>
                </button>
              </li>
            ))}
          </ol>
          <div className="ufd-art" aria-hidden="true">
            <span className="blob b1" /><span className="blob b2" />
            <div className="card"><span className="head" /><span className="body" /><span className="line" /><span className="line s" /></div>
            <span className="plus"><UserPlus className="w-4 h-4" /></span>
          </div>
        </aside>

        <section className="ufd-main">
          <button type="button" className="ufd-close" onClick={onClose} aria-label="Close" disabled={loading} data-testid="user-form-close"><X className="w-5 h-5" /></button>
          <div className="ufd-body">
            <div className="ufd-progress-label" data-testid="user-form-progress">Step {step} of 3</div>
            <div className="ufd-progress" aria-hidden="true">{[1, 2, 3].map((n) => <span key={n} data-on={n <= step ? 'true' : 'false'} />)}</div>
            <h3 className="ufd-h">{heading[0]}</h3>
            <p className="ufd-sub">{heading[1]}</p>

            {generalError && (
              <div className="ufd-general-error" role="alert" data-testid="user-form-general-error"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{generalError}</span></div>
            )}

            <div className="ufd-panel" key={step} data-dir={dir} data-testid={`user-form-panel-${step}`}>
              {step === 1 && (
                <>
                  <div className="ufd-field">
                    <label className="ufd-label" htmlFor="ufd-email">Email <span className="req">*</span></label>
                    <div className="ufd-input-wrap"><Mail />
                      <input id="ufd-email" type="email" className={`ufd-input ${errors.email ? 'error' : ''}`} value={formData.email} disabled={isEdit}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="user@example.com" autoComplete="off" data-testid="user-form-email" />
                    </div>
                    {!isEdit && !errors.email && <div className="ufd-help">User will receive an invitation email to set their password.</div>}
                    {errors.email && <div className="ufd-err" data-testid="user-form-email-error">{errors.email}</div>}
                  </div>
                  <div className="ufd-grid2">
                    <div className="ufd-field">
                      <label className="ufd-label" htmlFor="ufd-first">First Name <span className="req">*</span></label>
                      <div className="ufd-input-wrap"><User />
                        <input id="ufd-first" type="text" className={`ufd-input ${errors.first_name ? 'error' : ''}`} value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="John" data-testid="user-form-first-name" />
                      </div>
                      {errors.first_name && <div className="ufd-err">{errors.first_name}</div>}
                    </div>
                    <div className="ufd-field">
                      <label className="ufd-label" htmlFor="ufd-last">Last Name <span className="req">*</span></label>
                      <div className="ufd-input-wrap"><User />
                        <input id="ufd-last" type="text" className={`ufd-input ${errors.last_name ? 'error' : ''}`} value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Doe" data-testid="user-form-last-name" />
                      </div>
                      {errors.last_name && <div className="ufd-err">{errors.last_name}</div>}
                    </div>
                  </div>

                  {!isEdit && (
                    <div className="ufd-notice" data-testid="user-form-otp-notice">
                      <span className="ufd-notice-ico"><MailCheck className="w-4 h-4" /></span>
                      <div>
                        <strong>Password Setup via Email</strong>
                        <p>User will receive an email with a verification code (OTP) to verify their account and set their own password. No password is required here.</p>
                      </div>
                      <Info className="w-5 h-5 info" />
                    </div>
                  )}

                  <div className="ufd-divider" />
                  <div className="ufd-field">
                    <div className="ufd-label">Role <span className="req">*</span> <span className="hint">(Select one role)</span></div>
                    <div className={`ufd-roles ${errors.role_id ? 'error' : ''}`} role="radiogroup" aria-label="Role" data-testid="user-form-roles">
                      {roles.map((role) => {
                        const rc = roleCard(role.id); const on = formData.role_id === role.id;
                        return (
                          <button type="button" key={role.id} className="ufd-role" role="radio" aria-checked={on} onClick={() => handleRoleSelect(role.id)}
                            style={{ '--rc-bg': rc.bg, '--rc-fg': rc.fg }} data-testid={`user-form-role-${role.id}`}>
                            <span className="ufd-role-ico"><rc.Icon className="w-5 h-5" /></span>
                            <span className="min-w-0">
                              <span className="ufd-role-name block">{role.name}</span>
                              {role.description && <span className="ufd-role-desc block">{role.description}</span>}
                            </span>
                            <span className="ufd-radio" aria-hidden="true" />
                          </button>
                        );
                      })}
                    </div>
                    {errors.role_id && <div className="ufd-err" data-testid="user-form-role-error">{errors.role_id}</div>}
                    {isEdit && isLegacyRole(storedPrimary, compat) && (
                      <p className="ufd-help" data-testid="user-form-legacy-role-note">
                        <Info className="inline w-4 h-4 mr-1 -mt-0.5" />Stored role key <code>{storedPrimary}</code> ({roleLabel(storedPrimary, compat)}) is shown as {roleLabel(formData.role_id, compat)}. It is preserved as-is on save unless you pick a different role.
                      </p>
                    )}
                  </div>
                  {isEdit && canHoldAdditional(formData.role_id, compat) && (() => {
                    const isQcPrimary = canonicalRole(formData.role_id, compat) === compat.qc_role;
                    const offer = offerableAdditional(formData.role_id, qcScope, compat);
                    const scopeText = qcScope.length ? qcScope.map((s) => s[0].toUpperCase() + s.slice(1)).join(' + ') : 'not set';
                    return (
                    <div className="ufd-field" data-testid="user-form-additional-roles">
                      <div className="ufd-label">Additional roles <span className="hint">(optional — same workstream only)</span></div>
                      <p className="ufd-help">Tax stays with Tax; Title Search / Typing stays with Title. QC Specialist can be added to any processor role; a QC primary can take processor roles only within its QC Product Scope{isQcPrimary ? <> (currently <strong data-testid="user-form-qc-scope">{scopeText}</strong>)</> : null}.</p>
                      {isQcPrimary && !qcScope.length && (
                        <p className="ufd-err" data-testid="user-form-scope-required"><AlertTriangle className="inline w-4 h-4 mr-1 -mt-0.5" />Set this user's QC Product Scope (pencil icon in the QC Scope column) before adding processing roles.</p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {offer.map((id) => {
                          const name = roles.find((r) => r.id === id)?.name || roleLabel(id, compat);
                          const on = formData.additional_role_ids.includes(id);
                          const reason = on ? null : blockReason(id, formData.role_id, formData.additional_role_ids, compat);
                          return (
                            <label key={id} title={reason || undefined}
                              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm ${reason ? 'cursor-not-allowed opacity-50 border-slate-200 bg-slate-50 text-slate-400' : on ? 'cursor-pointer border-blue-500 bg-blue-50 text-blue-800' : 'cursor-pointer border-slate-300 bg-white text-slate-700'}`}
                              data-testid={`additional-role-${id}`} data-blocked={reason ? 'true' : undefined}>
                              <input type="checkbox" checked={on} disabled={!!reason} onChange={(e) => toggleAdditional(id, e.target.checked)} />
                              {name}
                            </label>
                          );
                        })}
                        {offer.length === 0 && !(isQcPrimary && !qcScope.length) && <span className="text-sm text-slate-500" data-testid="user-form-no-additional">No compatible additional roles for this primary.</span>}
                      </div>
                      {removedRoles.length > 0 && (
                        <div className="ufd-err mt-2" role="status" data-testid="user-form-removed-roles">
                          <AlertTriangle className="inline w-4 h-4 mr-1 -mt-0.5" />
                          {removedRoles.map((r) => (
                            <div key={r.role} data-testid={`removed-role-${r.role}`}>{r.flagged ? 'Flagged on this account: ' : 'Removed: '}<strong>{roleLabel(r.role, compat)}</strong> — {r.reason}{r.flagged ? ' It will be removed when you save.' : ''}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </>
              )}

              {step === 2 && (
                <>
                  <div className="ufd-summary-card" data-testid="user-form-role-summary">
                    <span className="ufd-role-ico" style={{ '--rc-bg': roleCard(formData.role_id).bg, '--rc-fg': roleCard(formData.role_id).fg }}><RoleIcon className="w-5 h-5" /></span>
                    <div>
                      <div className="ufd-role-name">{selectedRole?.name || 'No role selected'}</div>
                      <div className="ufd-role-desc">{selectedRole?.description || 'Go back to Step 1 to choose a role.'}</div>
                      {Array.isArray(selectedRole?.permissions) && selectedRole.permissions.length > 0 && (
                        <div className="ufd-perm-list mt-3">
                          {selectedRole.permissions.slice(0, 12).map((pm, i) => <span key={i} className="ufd-perm">{typeof pm === 'string' ? pm : `${pm.resource}:${pm.action}`}</span>)}
                          {selectedRole.permissions.length > 12 && <span className="ufd-perm">+{selectedRole.permissions.length - 12} more</span>}
                        </div>
                      )}
                    </div>
                  </div>

                  {needsTeam && (
                    <div className="ufd-field">
                      <label className="ufd-label" htmlFor="ufd-team">Team Assignment <span className="req">*</span> <span className="hint">(Required for leave routing)</span></label>
                      <div className="ufd-input-wrap"><Users />
                        <select id="ufd-team" className={`ufd-input ufd-select ${errors.team_id ? 'error' : ''}`} value={formData.team_id}
                          onChange={(e) => setFormData((prev) => ({ ...prev, team_id: e.target.value }))} data-testid="team-assignment-select">
                          <option value="">Select a team...</option>
                          {teams.length === 0 && <option value="" disabled>No teams available — contact admin</option>}
                          {teams.map((team) => <option key={team.id} value={team.id}>{team.name} {team.leader_name ? `(Leader: ${team.leader_name})` : ''}</option>)}
                        </select>
                      </div>
                      {errors.team_id && <div className="ufd-err" data-testid="user-form-team-error">{errors.team_id}</div>}
                    </div>
                  )}

                  {qcApplies && (
                    <label className="ufd-toggle-row" data-on={formData.requires_qc ? 'true' : 'false'} data-testid="qc-required-toggle-label">
                      <div>
                        <strong>QC Required</strong>
                        <p>{formData.requires_qc ? 'Completed orders will be routed to QC review before delivery.' : 'Completed orders will skip QC and go directly to delivery.'}</p>
                      </div>
                      <input type="checkbox" checked={formData.requires_qc} onChange={(e) => setFormData((prev) => ({ ...prev, requires_qc: e.target.checked }))} data-testid="qc-required-toggle" />
                      <span className="ufd-switch" aria-hidden="true" />
                    </label>
                  )}

                  {financeApplies && (
                    <label className="ufd-toggle-row" data-on={formData.view_search_pricing_margin ? 'true' : 'false'} data-testid="finance-visibility-toggle-label">
                      <div>
                        <strong>View Search Pricing &amp; Margin</strong>
                        <p>{formData.view_search_pricing_margin ? 'Can view Client Price, Total Billed, Internal Cost and Gross Margin on Search Fulfillment (read-only).' : 'Confidential pricing, cost and margin values are hidden for this user (default).'}</p>
                      </div>
                      <input type="checkbox" checked={formData.view_search_pricing_margin} onChange={(e) => setFormData((prev) => ({ ...prev, view_search_pricing_margin: e.target.checked }))} data-testid="finance-visibility-toggle" />
                      <span className="ufd-switch" aria-hidden="true" />
                    </label>
                  )}

                  {!needsTeam && !qcApplies && (
                    <p className="ufd-help" data-testid="user-form-no-extra">No additional assignments are required for this role.</p>
                  )}
                </>
              )}

              {step === 3 && (
                <>
                  <div className="ufd-review" data-testid="user-form-review">
                    <div className="ufd-review-row"><span className="k">Email</span><span className="v"><Mail className="w-4 h-4 text-slate-400" />{formData.email}</span></div>
                    <div className="ufd-review-row"><span className="k">Name</span><span className="v">{formData.first_name} {formData.last_name}</span></div>
                    <div className="ufd-review-row"><span className="k">Role</span><span className="v"><RoleIcon className="w-4 h-4" style={{ color: roleCard(formData.role_id).fg }} />{selectedRole?.name || '—'}</span></div>
                    {formData.additional_role_ids.length > 0 && (
                      <div className="ufd-review-row" data-testid="review-additional-roles"><span className="k">Additional roles</span><span className="v">{formData.additional_role_ids.map((id) => roles.find((r) => r.id === id)?.name || roleLabel(id, compat)).join(', ')}</span></div>
                    )}
                    {removedRoles.length > 0 && (
                      <div className="ufd-review-row" data-testid="review-removed-roles"><span className="k">Removed (incompatible)</span><span className="v text-amber-700">{removedRoles.map((r) => roleLabel(r.role, compat)).join(', ')}</span></div>
                    )}
                    {isEdit && isLegacyRole(storedPrimary, compat) && primaryToSend === storedPrimary && (
                      <div className="ufd-review-row" data-testid="review-legacy-preserved"><span className="k">Stored key</span><span className="v">{storedPrimary} (preserved)</span></div>
                    )}
                    {needsTeam && <div className="ufd-review-row"><span className="k">Team</span><span className="v">{selectedTeam ? selectedTeam.name : '—'}</span></div>}
                    {qcApplies && <div className="ufd-review-row"><span className="k">QC Required</span><span className="v">{formData.requires_qc ? 'Yes — routed to QC review' : 'No — delivered directly'}</span></div>}
                    {!isEdit && <div className="ufd-review-row"><span className="k">Password</span><span className="v">Set by the user via OTP invitation email</span></div>}
                  </div>
                  {!isEdit && (
                    <div className="ufd-notice">
                      <span className="ufd-notice-ico"><Send className="w-4 h-4" /></span>
                      <div><strong>Invitation email</strong><p>An email with a one-time verification code will be sent to <b>{formData.email}</b> so the user can verify the account and set a password.</p></div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </section>

        <footer className="ufd-footer">
          {step > 1 && (
            <button type="button" className="ufd-btn ghost" onClick={goBack} disabled={loading} data-testid="user-form-back"><ArrowLeft className="w-4 h-4" />Back</button>
          )}
          <span className="spacer" />
          <button type="button" className="ufd-btn secondary" onClick={onClose} disabled={loading} data-testid="user-form-cancel">Cancel</button>
          {step < 3 ? (
            <button key="next" type="button" className="ufd-btn primary" onClick={goNext} data-testid="user-form-next">Continue<ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button key="submit" type="submit" className="ufd-btn primary" disabled={loading} data-testid="user-form-submit">
              {loading ? <><Loader2 className="w-4 h-4 ufd-spin" />Saving…</> : isEdit ? <><Check className="w-4 h-4" />Update User</> : <><UserPlus className="w-4 h-4" />Create User</>}
            </button>
          )}
        </footer>
      </form>
    </>
  );
};

export default UserFormDialog;
